import { registerWeapon } from '../../weapons';
import { SMALL_CRATER } from '../../terrain/crater-shapes';
import { defaultExplosion } from '../../game/explosion';
import { KOMA_RIBBON, updateKomaRibbon } from './constants';
import type { RibbonPoint } from './constants';

// ─── Variant D: Skipper ─────────────────────────────────────────────────────
// On impact, bounces off terrain at reflection angle (like Bouncer).
// 3 bounces with small explosions (craters + damage) at each skip point.
// Wind affects each bounce arc.
// Area denial across a line — good for horizontal pressure.

const BOUNCE = {
    SPEED_MULT: 0.6,       // inherit this fraction of primary's speed
    GRAVITY: 0.10,         // gravity during each arc
    BOUNCE_COUNT: 3,       // total bounces before final detonation
    BOUNCE_RETENTION: 0.75, // velocity retained per bounce
    BOUNCE_BLAST: 25,      // explosion radius at each skip — reduced for quick puffs
    BOUNCE_DAMAGE: 8,      // damage at each skip
    BOUNCE_KNOCKBACK: 0.04,
    WIND_SENSITIVITY: 1.5, // wind multiplier during arcs
    FINAL_BLAST: 50,       // bigger explosion on last bounce
    FINAL_DAMAGE: 12,
    TIMEOUT: 300,          // safety timeout (5s)
};

export const GYRO_SKIPPER = registerWeapon({
    id: 'gyro_skipper',
    name: 'Skipper',
    description: '',
    tier: 'SUB',
    rarity: 'common',
    projectileRadius: 8,
    projectileSpeed: 0,
    color: 0x88CC77,
    gravity: BOUNCE.GRAVITY,
    bounces: BOUNCE.BOUNCE_COUNT,
    bounceEnergyRetention: BOUNCE.BOUNCE_RETENTION,
    damage: BOUNCE.FINAL_DAMAGE,
    blastRadius: BOUNCE.FINAL_BLAST,
    knockbackMult: BOUNCE.BOUNCE_KNOCKBACK,
    knockbackRadius: 1.0,
    shakeIntensity: 3.0,
    particleScale: 1.0,
    craterShape: SMALL_CRATER,
    projectileShape: 'koma',
    onTick(proj, state, fx) {
        const bs = proj.behaviorState;
        bs.lifetime = (bs.lifetime || 0) + 1;

        // Apply inherited velocity on first frame — launch into a bounce arc
        if (bs.lifetime === 1 && bs.inheritedVx !== undefined) {
            proj.vx = bs.inheritedVx * BOUNCE.SPEED_MULT;
            proj.vy = -3; // slight upward pop for first arc
        }

        // Wind pushes during arcs
        proj.vx += state.wind * BOUNCE.WIND_SENSITIVITY;

        // Ribbon trail — shared with Wind Rider (visual-only, in visualState)
        if (!proj.visualState) proj.visualState = {};
        if (!proj.visualState.ribbon) proj.visualState.ribbon = [];
        const ribbon = proj.visualState.ribbon as RibbonPoint[];
        const speed = Math.sqrt(proj.vx * proj.vx + proj.vy * proj.vy);
        updateKomaRibbon(ribbon, proj.x, proj.y, bs.lifetime, speed, state.wind);

        // Safety timeout
        if (bs.lifetime >= BOUNCE.TIMEOUT) {
            return 'detonate';
        }

        return null;
    },
    onBounce(proj, x, y, state, fx) {
        // Track bounce count for announcements
        const bs = proj.behaviorState;
        bs.bounceCount = (bs.bounceCount || 0) + 1;

        // Trim ribbon on bounce — keeps it short so it rebuilds fresh per arc
        if (proj.visualState?.ribbon) {
            const ribbon = proj.visualState.ribbon as RibbonPoint[];
            ribbon.length = Math.min(KOMA_RIBBON.BOUNCE_TRIM, ribbon.length);
        }
        fx.push({ type: 'announce_world', text: `SKIP x${bs.bounceCount}!`, x, y: y - 30, color: 0x88CC77, fontSize: 20, hold: 0.4 });

        // Small explosion at each skip point via defaultExplosion
        // Temporarily swap weapon stats for scaled-down skip explosion
        const origWeapon = proj.weapon;
        proj.weapon = {
            ...origWeapon,
            damage: BOUNCE.BOUNCE_DAMAGE,
            blastRadius: BOUNCE.BOUNCE_BLAST,
            knockbackMult: BOUNCE.BOUNCE_KNOCKBACK,
            particleScale: 0.15,
            shakeIntensity: 8.0,
        };
        const result = defaultExplosion(proj, x, y, state, fx);
        proj.weapon = origWeapon;

        return {
            spawns: [],
            knockedPlayers: result.knockedPlayers,
            hitPlayers: result.hitPlayers,
            damages: result.damages,
        };
    },
});
